import { useEffect, useMemo, useState } from "react";
import {
  getClientConversionLists,
  getClientLists,
} from "../../services/configurations";
import { useAppSelector } from "../../store/hook";
import moment from "moment";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatter, numFormatter } from "../../helpers/currency";
import Select from "react-select";
import { DatePicker } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import "antd/dist/antd.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";
import logo from "../../logo.svg";

const { RangePicker } = DatePicker;

interface ClientConversionData {
  totalEnquiries: number;
  totalLost: number;
  // Add other properties that exist on the client objects
  [key: string]: any; // This allows for additional unknown properties
}

export const ConversionDashboard = () => {
  const user = useAppSelector((state) => state.user.user);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [clientList, setClientList] = useState([]);
  const [filteredClientList, setFilteredClientList] = useState<
    ClientConversionData[]
  >([]);
  const [selectedClients, setSelectedClients] = useState<any[]>([]);
  const month = useMemo(() => {
    let retLabel = 0;
    switch (searchParams.get("filter")) {
      case "thismonths":
        retLabel = 0;
        break;
      case "1months":
        retLabel = 1;
        break;
      case "3months":
        retLabel = 3;
        break;
      case "6months":
        retLabel = 6;
        break;
      case "9months":
        retLabel = 9;
        break;
      case "12months":
        retLabel = 12;
        break;
    }
    return retLabel;
  }, [searchParams]);
  const [dateFilter, setDateFilter] = useState({
    startDate: month
      ? moment().subtract(month, "months").startOf("month").format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD"),
    endDate: month
      ? moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD"),
  });
  const [selectedQuater, setSelectedQuater] = useState(
    searchParams.get("filter")
      ? searchParams.get("filter") ?? "thismonths"
      : "thismonths"
  );
  useEffect(() => {
    if (user?.uniqueId) {
      getData();
    }
  }, [user, dateFilter]);

  useEffect(() => {
    if (selectedClients.length > 0) {
      const filtered = clientList.filter((item: any) =>
        selectedClients.some((client: any) => client.value === item.uniqueid)
      );
      setFilteredClientList(filtered);
    } else {
      setFilteredClientList(clientList);
    }
  }, [selectedClients, clientList]);

  const getData = () => {
    setIsLoading(true);
    if (!dateFilter?.startDate || !dateFilter?.endDate) {
      console.warn("Both startDate and endDate are required");
      //   toast.warn('Please select both start and end dates');
      return;
    }

    console.log("Fetching data with params:", {
      userId: user?.uniqueId,
      dates: dateFilter,
    });

    getClientConversionLists(
      user?.uniqueId,
      `?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}&page=1&results=20000`
    )
      .then((success) => {
        console.log("API Response:", success);
        if (success?.clinics) {
          setClientList(success.clinics);
          setFilteredClientList(success.clinics);
        } else {
          console.warn("Unexpected response format:", success);
          toast.error("Unexpected response format received");
        }
      })
      .catch((error) => {
        console.error("Error fetching client conversion lists:", {
          error,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        toast.error(
          error?.response?.data?.message ||
            "There's an error in fetching the list."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // utils.ts or inside your component
  const calculateConversionRate = (
    totalEnquiries: number,
    acquisitions: number
  ): string => {
    if (!totalEnquiries || totalEnquiries === 0) {
      // if totalEnquiries = 0, conversion should be "0.0%"
      return "0.0%";
    }
    const rate = (acquisitions / totalEnquiries) * 100;
    return `${rate.toFixed(1)}%`; // one decimal place
  };

  const averageConversionRate = useMemo(() => {
    if (filteredClientList.length === 0) return "0.0%";

    const totalRates = filteredClientList.reduce((sum, item) => {
      if (!item.totalEnquiries || item.totalEnquiries === 0) return sum;
      const rate = (item.totalAcquisitions || 0) / item.totalEnquiries;
      return sum + rate;
    }, 0);

    const avgRate = (totalRates / filteredClientList.length) * 100;
    return `${avgRate.toFixed(1)}%`;
  }, [filteredClientList]);

  const addWatermark = (pdf: jsPDF) => {
    const pageCount = pdf.getNumberOfPages();
    const lines = ["Xcare"];

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Save the current graphics state
      pdf.saveGraphicsState();

      // Set watermark properties
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(28); // Slightly smaller font for two lines
      pdf.setTextColor(200, 200, 200, 0.3);

      // Save state before rotation
      pdf.saveGraphicsState();

      // Set opacity
      pdf.setGState(new (pdf as any).GState({ opacity: 0.1 }));

      // Move to center
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;

      // Calculate total height of both lines
      const lineHeight = 35; // Space between lines

      // Draw each line of text
      lines.forEach((line, index) => {
        pdf.text(line, centerX, centerY - lineHeight / 2 + index * lineHeight, {
          align: "center",
          angle: 45,
        });
      });

      // Restore the graphics state
      pdf.restoreGraphicsState();
    }
  };

  const addFooter = (pdf: jsPDF) => {
    const pageCount = pdf.getNumberOfPages();
    const footerText = `© ${new Date().getFullYear()} Xcare Pro. All rights reserved.`;

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add footer line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(20, pageHeight - 30, pageWidth - 20, pageHeight - 30);

      // Add footer text
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(footerText, pageWidth / 2, pageHeight - 20, { align: "center" });

      // Add page number
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 20, {
        align: "right",
      });
    }
  };

  const exportConversionTableToPDF = async () => {
    // ✅ Hide avatar initials temporarily
    const avatarInitials = document.querySelectorAll(".avatar-initials");
    const originalDisplays: string[] = [];
    avatarInitials.forEach((el) => {
      const htmlEl = el as HTMLElement;
      originalDisplays.push(htmlEl.style.display);
      htmlEl.style.display = "none";
    });

    try {
      const table = document.getElementById("conversion-table");
      if (!table) {
        console.error("conversion-table not found");
        return;
      }

      // ✅ Load the logo image
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = logo as string;

      // Wait for logo to load
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Convert image to base64
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const imgData = canvas.toDataURL("image/png");

      // ✅ Collect header cells
      const headerCells = table.querySelectorAll("thead tr th");
      const headers: string[] = [];
      headerCells.forEach((th) => {
        headers.push((th.textContent || "").trim());
      });

      // ✅ Collect body rows
      const bodyRows = table.querySelectorAll("tbody tr");
      const body: string[][] = [];

      bodyRows.forEach((tr) => {
        const cells = tr.querySelectorAll("td");
        const rowData: string[] = [];

        cells.forEach((cell, cellIndex) => {
          if (cellIndex === 0) {
            // first column: only text from `.text-black`
            const nameDiv = cell.querySelector(".text-black");
            rowData.push(nameDiv ? nameDiv.textContent?.trim() || "" : "");
          } else {
            rowData.push((cell.textContent || "").trim());
          }
        });

        body.push(rowData);
      });

      // ✅ Handle TOTAL row separately
      const totalRowEl = table.querySelector("tr.fw-bold");
      if (totalRowEl) {
        const totalCells = totalRowEl.querySelectorAll("td");
        const totalData: string[] = [];
        totalCells.forEach((cell, cellIndex) => {
          if (cellIndex === 0) {
            const nameDiv = cell.querySelector(".text-black");
            totalData.push(nameDiv ? nameDiv.textContent?.trim() || "" : "");
          } else {
            totalData.push((cell.textContent || "").trim());
          }
        });
        body.push(totalData);
      }

      // ✅ Generate PDF
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Add logo
      pdf.addImage(imgData, "PNG", 10, 5, 40, 15);

      // Add title
      pdf.setFontSize(16);
      pdf.setTextColor(25, 78, 91);
      pdf.text("Conversion Report", pageWidth / 2, 15, { align: "center" });

      // ✅ Render table
      autoTable(pdf, {
        head: [headers],
        body: body,
        startY: 25,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 3,
          valign: "middle",
          halign: "center",
          overflow: "linebreak",
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [0, 90, 90], // teal header
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
        },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
        },
        didDrawPage: (dataArg) => {
          const pageHeight = pdf.internal.pageSize.getHeight();
          pdf.setFontSize(9);
          pdf.setTextColor(100);
          pdf.text(
            `Page ${dataArg.pageNumber}`,
            dataArg.settings.margin.left,
            pageHeight - 5
          );
        },
        didParseCell: (data) => {
          // Highlight TOTAL row (last row)
          if (data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [230, 230, 230]; // light gray
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      // ✅ Save PDF
      addWatermark(pdf);
      addFooter(pdf);
      pdf.save("conversion_report.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      // ✅ Restore initials
      avatarInitials.forEach((el, index) => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.display = originalDisplays[index] || "";
      });
    }
  };

  return (
    <>
      <section className="middle-navbar px-30">
        <div className="p-0 container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex flex-row">
                <div className="nav-title-txt">Conversion Dashboard</div>
              </div>
            </div>

            <div className="d-flex flex-column">
              <div className="d-flex gap-3 align-items-center">
                <div className="d-flex gap-2">
                  <div className="dashb-filters">
                    <RangePicker
                      className="pdf-download-button h-[38px] rounded-[10px]"
                      dropdownClassName="single-calendar"
                      getPopupContainer={(trigger: any) =>
                        trigger.parentElement!
                      }
                      value={[
                        dateFilter.startDate
                          ? moment(dateFilter.startDate, "YYYY-MM-DD")
                          : null,
                        dateFilter.endDate
                          ? moment(dateFilter.endDate, "YYYY-MM-DD")
                          : null,
                      ]}
                      onChange={(dates: any) => {
                        if (dates && dates[0] && dates[1]) {
                          setDateFilter({
                            startDate: dates[0].format("YYYY-MM-DD"),
                            endDate: dates[1].format("YYYY-MM-DD"),
                          });
                        } else {
                          setDateFilter({
                            startDate: moment().format("YYYY-MM-DD"),
                            endDate: moment().format("YYYY-MM-DD"),
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="dashb-filters">
                    <ReactDatePicker
                      className="form-control col-2"
                      selected={
                        dateFilter?.startDate
                          ? moment(dateFilter.startDate).toDate()
                          : null
                      }
                      onChange={(dates) => {
                        const [start, end] = dates;
                        if (start === null && end === null) {
                          const currentDate = moment();
                          setDateFilter({
                            startDate: currentDate
                              .startOf("month")
                              .format("YYYY-MM-DD"),
                            endDate: currentDate
                              .endOf("month")
                              .format("YYYY-MM-DD"),
                          });
                        } else {
                          setDateFilter({
                            startDate: start
                              ? moment(start)
                                  .startOf("month")
                                  .format("YYYY-MM-DD")
                              : "",
                            endDate: end
                              ? moment(end).endOf("month").format("YYYY-MM-DD")
                              : "",
                          });
                        }
                      }}
                      startDate={
                        dateFilter?.startDate
                          ? moment(dateFilter.startDate).toDate()
                          : null
                      }
                      endDate={
                        dateFilter?.endDate
                          ? moment(dateFilter.endDate).toDate()
                          : null
                      }
                      selectsRange
                      showMonthYearPicker
                      dateFormat="MMM/yyyy"
                      placeholderText="Select date range"
                      isClearable={
                        !(
                          dateFilter?.startDate ===
                            moment().startOf("month").format("YYYY-MM-DD") &&
                          dateFilter?.endDate ===
                            moment().endOf("month").format("YYYY-MM-DD")
                        )
                      }
                    />
                    <select
                      className="form-select"
                      aria-label="Default select example"
                      onChange={(e) => {
                        setSelectedQuater(e.target.value);
                        switch (e.target.value) {
                          case "thismonths":
                            setDateFilter({
                              startDate: moment()
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment().format("YYYY-MM-DD"),
                            });
                            break;
                          case "1months":
                            setDateFilter({
                              startDate: moment()
                                .subtract(1, "months")
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment()
                                .subtract(1, "months")
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                            });
                            break;
                          case "3months":
                            setDateFilter({
                              startDate: moment()
                                .subtract(3, "months")
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment()
                                .subtract(1, "months")
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                            });
                            break;
                          case "6months":
                            setDateFilter({
                              startDate: moment()
                                .subtract(6, "months")
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment()
                                .subtract(1, "months")
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                            });
                            break;
                          case "9months":
                            setDateFilter({
                              startDate: moment()
                                .subtract(9, "months")
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment()
                                .subtract(1, "months")
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                            });
                            break;
                          case "12months":
                            setDateFilter({
                              startDate: moment()
                                .subtract(12, "months")
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                              endDate: moment()
                                .subtract(1, "months")
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                            });
                            break;
                        }
                      }}
                      value={selectedQuater}
                    >
                      <option value="thismonths">This Month</option>
                      <option value="1months">Last Month</option>
                      <option value="3months">Last 3 Months</option>
                      <option value="6months">Last 6 Months</option>
                      <option value="9months">Last 9 Months</option>
                      <option value="12months">Last 12 Months</option>
                    </select>
                  </div>
                </div>
              </div>
              <small className="pt-1 text-success">
                From {dateFilter?.startDate} to {dateFilter?.endDate}
              </small>
            </div>
          </div>
        </div>
      </section>

      <section className="client-table-details-sec px-30 pt-4">
        <div className="container">
          <div className="row mb-4">
            <div className="col-10">
              <Select
                isMulti
                options={
                  clientList.map((item: any) => ({
                    value: item.uniqueid,
                    label: item.name,
                  })) as { value: any; label: any }[]
                }
                value={selectedClients}
                onChange={(newValue: any) => {
                  setSelectedClients(newValue);
                }}
                placeholder="Select Clients"
                className="w-100"
              />
            </div>
            <div className="col-2 d-flex align-items-center">
              <button
                onClick={exportConversionTableToPDF}
                className="btn btn-primary w-100"
              >
                <i className="bi bi-download me-2"></i> Export PDF
              </button>
            </div>
          </div>
          <div className="table-responsive" id="conversion-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Total Enquiries</th>
                  <th>Lost</th>
                  <th>New Acquisitions</th>
                  <th>Conversion Rate</th>
                  <th>New Reviews</th>
                  {/* <th>Reviews</th> */}
                  <th></th>
                </tr>
              </thead>
              {!isLoading && (
                <tbody>
                  {filteredClientList?.map((item: any) => {
                    const roiTrend =
                      item?.totalRevenueGoals <
                      item?.actualRevenue + item?.revenue;
                    return (
                      <tr>
                        <td>
                          <Link
                            to={`/newdashboard?clinic=${item.uniqueid}`}
                            className="d-flex align-items-center text-decoration-none"
                            onClick={() => {
                              sessionStorage.setItem(
                                "selectedClinic",
                                item.uniqueid
                              );
                            }}
                          >
                            <span className="avatar flex-shrink-0 avatar-48 me-2 ">
                              <span className="avatar-initials">
                                {item?.name?.charAt(0)}
                                {item?.name?.charAt(1)}
                              </span>
                            </span>
                            <div className="text-black">{item?.name}</div>
                          </Link>
                        </td>
                        <td>
                          {numFormatter.format(item?.totalEnquiries ?? 0)}
                          {/* {formatter.format(item?.budget ?? 0)}
                        {item?.budgetChart === "Positive" ? (
                          <i className="bi bi-graph-up-arrow text-success ml-2"></i>
                        ) : (
                          <i className="bi bi-graph-down-arrow text-danger ml-2"></i>
                        )} */}
                        </td>
                        <td>
                          {numFormatter.format(item?.totalLost ?? 0)}
                          {/* {item?.leadsChart === "Positive" ? (
                          <i className="bi bi-graph-up-arrow text-success ml-2"></i>
                        ) : (
                          <i className="bi bi-graph-down-arrow text-danger ml-2"></i>
                        )} */}
                        </td>
                        <td>
                          {numFormatter.format(item?.totalAcquisitions ?? 0)}
                          {/* {item?.acquisitionsChart === "Positive" ? (
                          <i className="bi bi-graph-up-arrow text-success ml-2"></i>
                        ) : (
                          <i className="bi bi-graph-down-arrow text-danger ml-2"></i>
                        )} */}
                        </td>
                        <td>
                          {calculateConversionRate(
                            item?.totalEnquiries,
                            item?.totalAcquisitions
                          )}
                          {/* {item?.revenueChart === "Positive" ? (
                          <i className="bi bi-graph-up-arrow text-success ml-2"></i>
                        ) : (
                          <i className="bi bi-graph-down-arrow text-danger ml-2"></i>
                        )} */}
                        </td>
                        {/* <td>
                        {!isNaN(
                          (item?.actualRevenue + item?.revenue) /
                            item?.marketingSpend
                        ) &&
                        item?.actualRevenue !== 0 &&
                        item?.marketingSpend !== 0
                          ? (
                              ((item?.actualRevenue + item?.revenue) /
                                item?.marketingSpend) *
                              100
                            ).toFixed(2) + "%"
                          : "-"}
                        {!isNaN(
                          (item?.actualRevenue + item?.revenue) /
                            item?.marketingSpend
                        ) &&
                          item?.actualRevenue !== 0 &&
                          item?.marketingSpend !== 0 && (
                            <>
                              {roiTrend ? (
                                <i className="bi bi-graph-up-arrow text-success ml-2"></i>
                              ) : (
                                <i className="bi bi-graph-down-arrow text-danger ml-2"></i>
                              )}
                            </>
                          )}
                      </td> */}
                        <td>{numFormatter.format(item?.review ?? 0)}</td>
                        <td>
                          {/* <button type="button" className="btn btn-outline-secondary"><i className="bi bi-gear"></i></button> */}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
              {!isLoading && filteredClientList?.length > 0 && (
                <tr className="fw-bold">
                  <td className="pl-2">
                    <div className="text-black">Total</div>
                  </td>
                  <td className="pl-2">
                    {numFormatter.format(
                      filteredClientList.reduce(
                        (sum, item) =>
                          sum + (Number(item?.totalEnquiries) || 0),
                        0
                      )
                    )}
                  </td>
                  <td className="pl-2">
                    {numFormatter.format(
                      filteredClientList.reduce(
                        (sum, item) => sum + (Number(item?.totalLost) || 0),
                        0
                      )
                    )}
                  </td>
                  <td className="pl-2">
                    {numFormatter.format(
                      filteredClientList.reduce(
                        (sum, item) =>
                          sum + (Number(item?.totalAcquisitions) || 0),
                        0
                      )
                    )}
                  </td>
                  <td className="pl-2">{averageConversionRate || 0}</td>
                  <td className="pl-2">
                    {numFormatter.format(
                      filteredClientList.reduce(
                        (sum, item) => sum + (Number(item?.review) || 0),
                        0
                      )
                    )}
                  </td>
                  <td className="pl-2"></td>
                </tr>
              )}
            </table>
            {isLoading && (
              <div className="w-full flex justify-center items-center">
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading conversion data...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
